import React, {useEffect, useRef, useState} from 'react';
import {Platform, StyleSheet, TouchableOpacity, View} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import MaterialCommunityIcon from 'react-native-vector-icons/MaterialCommunityIcons';

import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import {radii} from '@/Utils/radius';
import {ScreenName} from '@/navigators/StackConstants';
import {event} from '@/Utils/GAEvents';
import {
  useJourneyStore,
  JourneyCard as JourneyCardData,
} from '@/Store/journeyStore';
import {postJourneyAck} from '@/Hooks/Journey/useJourneySync';
import {msUntilExpiry} from '@/Utils/journeyGates';

interface Props {
  card: JourneyCardData;
}

// The backend sends deeplink.screen as the route VALUE (e.g. "Positions" --
// ScreenName.Puzzles's value, not its key), matching how navigation.navigate
// is called everywhere else on web (navigate(ScreenName.X, ...), never a raw
// enum key). Validate and navigate against ScreenName's VALUES, not its
// keys -- a key-based check would wrongly reject every screen whose key
// differs from its route value and silently fall back to Dashboard. Falls
// back to Dashboard for a screen this build doesn't know about (the backend
// is shared with mobile and evolves independently).
const isKnownScreen = (screen: string | null | undefined): boolean =>
  !!screen && Object.values(ScreenName).includes(screen);

// Web-only UX: the card floats above the dashboard instead of sitting inline
// in the banner flow, and quietly gets out of the way after 10s so it never
// blocks the page. Mobile keeps its inline top-slot card -- this file has no
// mobile equivalent (cc-learning-app has its own JourneyCard).
export const JOURNEY_CARD_AUTOHIDE_MS = 10000;
const TRANSITION_MS = 200;

// Ids the floating card has auto-hidden THIS PAGE LOAD -- a plain in-memory
// module-level Set, deliberately NOT store state. Auto-hide is local-only by
// spec: no ack, no markDismissed, nothing persisted (the card is still
// "active" server-side and must still show up on Dashboard's own
// activeCards() selector so a genuinely new card can still take the slot).
// Without this, the SAME card id would pop back up on every
// dashboard_focus/foreground refetch that returns the unchanged envelope.
// Resets on a full page reload, same as every other in-memory ref here.
const autoHiddenCardIds = new Set<string>();

// `position: 'fixed'` and viewport (`vw`) units are react-native-web-only
// values -- RN's ViewStyle type only allows 'absolute' | 'relative' for
// `position`, so this whole object is cast, same escape hatch CCLoader uses
// for its own viewport-fixed overlay. The slide-down + fade uses a plain CSS
// transition (Header's expiry chip does the same for its web-only polish)
// rather than an animation library -- nothing here needs a shared driver,
// and it keeps the mount/auto-hide sequencing trivial to reason about (and
// to test: no RAF-driven animation loop to fake).
const floatingStyle = {
  position: 'fixed',
  top: 80,
  right: 24,
  width: 360,
  maxWidth: '90vw',
  zIndex: 500,
} as any;

const webTransitionStyle = (entered: boolean) =>
  Platform.OS === 'web'
    ? ({
        transition: `opacity ${TRANSITION_MS}ms ease-out, transform ${TRANSITION_MS}ms ease-out`,
        opacity: entered ? 1 : 0,
        transform: [{translateY: entered ? 0 : -16}],
      } as any)
    : {opacity: entered ? 1 : 0};

// Dashboard "next step" nudge. Visual language borrowed from
// BannerCarousel/FocusCards' primitives (rounded panel, left accent bar,
// pill CTA) but in a neutral cream palette so it doesn't read as another
// promotional/tournament banner.
const JourneyCard = ({card}: Props) => {
  const navigation = useNavigation<any>();
  const markDismissed = useJourneyStore(state => state.markDismissed);
  const markImpressed = useJourneyStore(state => state.markImpressed);
  const serverClockOffset = useJourneyStore(state => state.serverClockOffset);

  // Already auto-hidden earlier this session (e.g. this instance remounted
  // -- a live game interrupted and ended -- for a card whose id we'd already
  // faded out): render nothing, no animation, straight away.
  const [isRendered, setIsRendered] = useState(
    () => !autoHiddenCardIds.has(card.id),
  );
  // Drives the CSS transition's end-state -- starts false so the first paint
  // renders the "hidden" (offscreen/transparent) style; a mount effect then
  // flips it true a tick later so the transition actually animates instead
  // of snapping straight to the visible state.
  const [entered, setEntered] = useState(false);

  const autoHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoHideRemainingMsRef = useRef(JOURNEY_CARD_AUTOHIDE_MS);
  const autoHideArmedAtRef = useRef(0);

  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Once-per-card-id guard lives in the STORE (impressedIds), not a
  // component ref -- a ref resets on every remount (Dashboard re-mount on
  // navigation, etc.) and was re-firing the impression ack + analytics event
  // for a card the user had already seen. Priority is logged but is not a
  // dep: it can't change without card.id also changing (a new card = a new
  // id), so keying re-firing on it would be wrong.
  useEffect(() => {
    if (useJourneyStore.getState().impressedIds.includes(card.id)) {
      return;
    }
    markImpressed(card.id);
    event({
      action: 'journey_card_shown',
      params: {card_id: card.id, priority: card.priority},
    });
    postJourneyAck(card.id, 'impression');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card.id]);

  useEffect(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    const delay = msUntilExpiry(card, serverClockOffset, Date.now());
    hideTimeoutRef.current = setTimeout(() => {
      markDismissed(card.id);
    }, delay);
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card.id, card.expires_at, serverClockOffset, markDismissed]);

  // Fades the floating card out, then stops rendering it -- LOCAL ONLY: no
  // ack, no markDismissed, nothing written to the store. The card is still
  // "active" as far as the store/activeCards() are concerned; only this
  // component (and the module-level autoHiddenCardIds guard) knows it's
  // gone, which is exactly what keeps it from popping back up on the next
  // dashboard_focus refetch of the same unchanged envelope.
  const fadeOutAndUnmount = () => {
    autoHiddenCardIds.add(card.id);
    setEntered(false);
    exitTimeoutRef.current = setTimeout(() => {
      exitTimeoutRef.current = null;
      setIsRendered(false);
    }, TRANSITION_MS);
  };

  // Entrance transition + the local-only 10s auto-hide timer. Hovering
  // pauses it (handleMouseEnter/Leave below) by banking whatever time is
  // left in autoHideRemainingMsRef and clearing the live timeout; leaving
  // re-arms a fresh timeout for exactly that remainder, so the total
  // "visible" time before auto-hiding is always JOURNEY_CARD_AUTOHIDE_MS
  // regardless of how long the pointer lingered over the card.
  useEffect(() => {
    if (!isRendered) {
      return undefined;
    }

    setEntered(true);

    autoHideRemainingMsRef.current = JOURNEY_CARD_AUTOHIDE_MS;
    autoHideArmedAtRef.current = Date.now();
    autoHideTimeoutRef.current = setTimeout(
      fadeOutAndUnmount,
      autoHideRemainingMsRef.current,
    );

    return () => {
      if (autoHideTimeoutRef.current) {
        clearTimeout(autoHideTimeoutRef.current);
        autoHideTimeoutRef.current = null;
      }
      if (exitTimeoutRef.current) {
        clearTimeout(exitTimeoutRef.current);
        exitTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card.id, isRendered]);

  const handleMouseEnter = () => {
    if (!autoHideTimeoutRef.current) {
      return;
    }
    clearTimeout(autoHideTimeoutRef.current);
    autoHideTimeoutRef.current = null;
    const elapsedMs = Date.now() - autoHideArmedAtRef.current;
    autoHideRemainingMsRef.current = Math.max(
      0,
      autoHideRemainingMsRef.current - elapsedMs,
    );
  };

  const handleMouseLeave = () => {
    if (autoHideTimeoutRef.current || !isRendered) {
      return;
    }
    autoHideArmedAtRef.current = Date.now();
    autoHideTimeoutRef.current = setTimeout(
      fadeOutAndUnmount,
      autoHideRemainingMsRef.current,
    );
  };

  const handleCtaPress = () => {
    event({
      action: 'journey_card_tapped',
      params: {card_id: card.id, priority: card.priority},
    });
    postJourneyAck(card.id, 'cta_tap');
    const targetScreen = isKnownScreen(card.deeplink?.screen)
      ? (card.deeplink!.screen as string)
      : ScreenName.Dashboard;
    navigation.navigate(targetScreen, card.deeplink?.params);
  };

  const handleDismiss = () => {
    markDismissed(card.id);
    postJourneyAck(card.id, 'dismiss');
    event({action: 'journey_card_dismissed', params: {card_id: card.id}});
  };

  if (!isRendered) {
    return null;
  }

  return (
    <View
      testID="journey-card-floating"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={[floatingStyle, webTransitionStyle(entered)]}>
      <View style={styles.outer}>
        <View style={styles.accentBar} />
        <View style={styles.content}>
          {card.dismissible && (
            <TouchableOpacity
              testID="journey-card-dismiss"
              onPress={handleDismiss}
              hitSlop={{top: 10, bottom: 10, left: 10, right: 10}}
              style={styles.closeBtn}>
              <MaterialCommunityIcon
                name="close"
                size={16}
                color={CCColors.TextColor.Grey}
              />
            </TouchableOpacity>
          )}

          <CCText style={styles.title} numberOfLines={2}>
            {card.title}
          </CCText>
          <CCText style={styles.body} numberOfLines={3}>
            {card.body}
          </CCText>

          <TouchableOpacity
            activeOpacity={0.88}
            onPress={handleCtaPress}
            style={styles.cta}>
            <CCText style={styles.ctaText}>{card.cta}</CCText>
            <CCText style={styles.ctaArrow}>{'→'}</CCText>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

export default JourneyCard;

const styles = StyleSheet.create({
  outer: {
    borderRadius: radii.panel,
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: CCColors.Primary.lightBrown,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOpacity: 0.12,
    shadowRadius: 10,
    shadowOffset: {width: 0, height: 6},
    elevation: 4,
  },
  accentBar: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    width: 5,
    backgroundColor: CCColors.Primary.Aqua,
  },
  content: {
    paddingTop: 14,
    paddingBottom: 14,
    paddingLeft: 19,
    paddingRight: 16,
    gap: 6,
  },
  closeBtn: {
    position: 'absolute',
    top: 10,
    right: 10,
    padding: 2,
    zIndex: 1,
  },
  title: {
    fontSize: 16.5,
    fontWeight: '900',
    lineHeight: 20,
    letterSpacing: -0.25,
    color: CCColors.Primary.Brown,
    paddingRight: 20,
  },
  body: {
    fontSize: 13,
    fontWeight: '500',
    lineHeight: 18,
    color: CCColors.TextColor.GreyText,
  },
  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 6,
    marginTop: 4,
    paddingVertical: 9,
    paddingHorizontal: 15,
    borderRadius: radii.chip,
    backgroundColor: CCColors.Primary.Default,
  },
  ctaText: {
    fontSize: 13.5,
    fontWeight: '800',
    color: CCColors.Primary.Brown,
  },
  ctaArrow: {
    fontSize: 15,
    color: CCColors.Primary.Brown,
  },
});
